import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CheckCircle2, KeyRound, Lock, Mail, Pencil, Phone, Save, Shield, User, X } from "lucide-react";
import PageShell from "../../components/dashboard/PageShell";
import PaymentGatewaySettings from "../../components/dashboard/PaymentGatewaySettings";
import StatCard from "../../components/dashboard/StatCard";
import StatCardGrid from "../../components/dashboard/StatCardGrid";
import Button from "../../components/ui/Button";
import { OPS_PERMISSIONS as P } from "../../config/permissions";
import { getRoleLabel } from "../../config/roles";
import usePermissions from "../../hooks/usePermissions";
import useRole from "../../hooks/useRole";
import { updateOpsProfile } from "../../services/profileApi";
import { updateProfile } from "../../store/slices/authSlice";
import { formatAccountStatus, getOpsUserDisplayName } from "../../utils/normalizeOpsUser";
import { showError, showSuccess } from "../../utils/toast";

const editableInputClass =
  "w-full rounded-lg border border-primary/10 bg-white px-3 py-2.5 text-sm text-primary shadow-sm transition-colors focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20 disabled:cursor-not-allowed disabled:bg-primary/[0.03] disabled:text-primary/70";

function profileFormFromUser(user) {
  return {
    first_name: user?.first_name ?? "",
    last_name: user?.last_name ?? "",
    email: user?.email ?? "",
    phone_number: user?.phone_number ?? "",
  };
}

function formatLastLogin(value) {
  if (!value) return "Never";
  const raw = String(value);
  if (raw.length >= 16) return `${raw.slice(0, 10)} ${raw.slice(11, 16)}`;
  return raw.slice(0, 10);
}

export default function SettingsPage() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const { hasPermission } = usePermissions();
  const { isSuperAdmin } = useRole();
  const canManageGateway = isSuperAdmin && hasPermission(P.SETTINGS_MANAGE);
  const [tab, setTab] = useState("profile");

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => profileFormFromUser(user));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!editing) {
      setForm(profileFormFromUser(user));
      setSaved(false);
    }
  }, [user, editing]);

  const baseline = useMemo(() => profileFormFromUser(user), [user]);

  const dirty =
    form.first_name.trim() !== baseline.first_name.trim() ||
    form.last_name.trim() !== baseline.last_name.trim() ||
    form.email.trim().toLowerCase() !== baseline.email.trim().toLowerCase() ||
    form.phone_number.trim() !== baseline.phone_number.trim();

  const canSubmit =
    form.first_name.trim() &&
    form.email.trim().includes("@") &&
    form.phone_number.trim();

  const update = (key, value) => {
    if (!editing) return;
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleEdit = () => {
    setForm(profileFormFromUser(user));
    setEditing(true);
    setSaved(false);
  };

  const handleCancel = () => {
    setForm(profileFormFromUser(user));
    setEditing(false);
    setSaved(false);
  };

  const handleSave = async () => {
    if (!editing || !canSubmit || !dirty || saving) return;

    setSaving(true);
    try {
      const updated = await updateOpsProfile(token, form, baseline);
      dispatch(updateProfile(updated));
      setEditing(false);
      setSaved(true);
      showSuccess("Profile updated.");
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      showError(err.message || "Could not update profile.");
    } finally {
      setSaving(false);
    }
  };

  const displayName = getOpsUserDisplayName({
    first_name: form.first_name,
    last_name: form.last_name,
    email: form.email || user?.email,
    name: user?.name,
  });

  return (
    <PageShell
      title="Settings"
      description={
        isSuperAdmin ? "Profile, payment gateway, and ops preferences." : "Profile and preferences."
      }
      actions={
        tab === "profile" && editing ? (
          <div className="flex items-center gap-2">
            {dirty && (
              <span className="hidden text-xs font-medium text-amber-600 sm:inline">Unsaved changes</span>
            )}
            <Button variant="ghost" size="sm" onClick={handleCancel} disabled={saving}>
              <X className="h-4 w-4" />
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={!dirty || !canSubmit} loading={saving}>
              {saved ? (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  Saved
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Save changes
                </>
              )}
            </Button>
          </div>
        ) : tab === "profile" ? (
          <Button size="sm" onClick={handleEdit}>
            <Pencil className="h-4 w-4" />
            Edit profile
          </Button>
        ) : null
      }
    >
      <div className="mb-5 flex flex-wrap gap-2">
        {[
          { id: "profile", label: "Profile", icon: User },
          ...(isSuperAdmin ? [{ id: "gateway", label: "Payment gateway", icon: KeyRound }] : []),
        ].map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold ${
              tab === item.id ? "bg-primary text-white" : "bg-white text-primary/50 ring-1 ring-primary/10"
            }`}
          >
            <item.icon className="h-3.5 w-3.5" />
            {item.label}
          </button>
        ))}
      </div>

      {tab === "gateway" && isSuperAdmin ? (
        <PaymentGatewaySettings canManage={canManageGateway} />
      ) : (
        <>
      <div className="mb-5 overflow-hidden rounded-xl border border-primary/[0.07] bg-gradient-to-br from-primary via-[#1a1512] to-collect p-5 text-white shadow-[0_4px_20px_rgba(127,29,29,0.25)] sm:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-lg font-bold ring-1 ring-white/20">
              {displayName
                .split(/\s+/)
                .slice(0, 2)
                .map((w) => w[0])
                .join("")
                .toUpperCase()}
            </span>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-white/50">
                SimCollect Ops
              </p>
              <h2 className="mt-0.5 text-lg font-bold sm:text-xl">{displayName}</h2>
              <p className="mt-1 text-xs text-white/60">
                {getRoleLabel(user?.role)} · {form.email || user?.email}
              </p>
            </div>
          </div>
          <span className="rounded-lg bg-emerald-400/20 px-2.5 py-1 text-[10px] font-bold text-emerald-100 ring-1 ring-emerald-300/20">
            {formatAccountStatus(user?.status).toUpperCase()}
          </span>
        </div>
      </div>

      <StatCardGrid cols={3} className="mb-5">
        <StatCard
          label="Role"
          value={getRoleLabel(user?.role)}
          icon={Shield}
          tone="primary"
          valueClassName="text-sm"
        />
        <StatCard
          label="Email"
          value={form.email || user?.email || "—"}
          icon={Mail}
          tone="emerald"
          valueClassName="text-sm"
        />
        <StatCard
          label="Last login"
          value={formatLastLogin(user?.last_login_at)}
          icon={CheckCircle2}
          tone="accent"
          valueClassName="text-sm"
        />
      </StatCardGrid>

      <div className="rounded-xl border border-primary/[0.07] bg-white p-5 shadow-sm sm:p-6">
        <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/[0.06] text-primary">
              <User className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-base font-bold text-primary">Account details</h2>
              <p className="mt-0.5 text-xs text-primary/45">
                {editing
                  ? "Update your name, email, and phone, then save."
                  : "Click Edit profile to change your name, email, or phone. Role stays read-only."}
              </p>
            </div>
          </div>
          {!editing ? (
            <Button size="sm" variant="ghost" onClick={handleEdit} className="sm:hidden">
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
          ) : null}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First name" icon={User}>
            <input
              className={editableInputClass}
              value={form.first_name}
              onChange={(e) => update("first_name", e.target.value)}
              placeholder="John"
              autoComplete="given-name"
              disabled={!editing}
            />
          </Field>
          <Field label="Last name (optional)" icon={User}>
            <input
              className={editableInputClass}
              value={form.last_name}
              onChange={(e) => update("last_name", e.target.value)}
              placeholder="Doe"
              autoComplete="family-name"
              disabled={!editing}
            />
          </Field>
          <Field label="Email" icon={Mail}>
            <input
              type="email"
              className={editableInputClass}
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="john.doe@example.com"
              autoComplete="email"
              disabled={!editing}
            />
          </Field>
          <Field label="Phone number" icon={Phone}>
            <input
              type="tel"
              className={editableInputClass}
              value={form.phone_number}
              onChange={(e) => update("phone_number", e.target.value)}
              placeholder="+233241234567"
              autoComplete="tel"
              disabled={!editing}
            />
          </Field>
          <Field label="Role" icon={Lock} className="sm:col-span-2">
            <div className="flex items-center justify-between gap-2 rounded-lg border border-primary/10 bg-primary/[0.03] px-3 py-2.5 text-sm text-primary/70">
              <span className="truncate font-medium text-primary">{getRoleLabel(user?.role)}</span>
              <Lock className="h-3.5 w-3.5 shrink-0 text-primary/30" />
            </div>
          </Field>
        </div>

        {editing && (
          <div className="mt-5 flex flex-wrap justify-end gap-2 border-t border-primary/[0.06] pt-4">
            <Button variant="ghost" size="sm" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={!dirty || !canSubmit} loading={saving}>
              <Save className="h-4 w-4" />
              Save changes
            </Button>
          </div>
        )}
      </div>
        </>
      )}
    </PageShell>
  );
}

function Field({ label, icon: Icon, children, className = "" }) {
  return (
    <div className={className}>
      <label className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-primary/50">
        {Icon ? <Icon className="h-3.5 w-3.5 text-primary/30" /> : null}
        {label}
      </label>
      {children}
    </div>
  );
}
